import type { AccountInfo } from '@azure/msal-browser';
import { msalInstance } from '../index';
import { authorize, type authorizeResponse } from '../services/authService';

const STORAGE_KEY = 'token';

let inflight: Promise<authorizeResponse> | null = null;

interface FmsClaims {
  username?: string;
}

const decodeFmsClaims = (token: string): FmsClaims | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as FmsClaims;
  } catch {
    return null;
  }
};

export const clearFmsToken = (): void => {
  localStorage.removeItem(STORAGE_KEY);
  inflight = null;
};

export const getFmsAuthorization = (
  account: AccountInfo
): Promise<authorizeResponse> => {
  if (inflight) return inflight;
  const abortController = new AbortController();
  inflight = authorize(abortController, { username: account.username })
    .then((response) => {
      if (response?.token) {
        localStorage.setItem(STORAGE_KEY, response.token);
      }
      return response;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

export const getFmsToken = async (): Promise<string> => {
  const account = msalInstance.getActiveAccount();
  if (!account) return '';

  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    const claims = decodeFmsClaims(stored);
    const sameUser =
      claims?.username?.toLowerCase() === account.username.toLowerCase();
    if (sameUser) return stored;
    localStorage.removeItem(STORAGE_KEY);
  }

  const response = await getFmsAuthorization(account);
  return response.token ?? '';
};
